export type SerialQueue = <T>(task: () => Promise<T>) => Promise<T>

/**
 * A promise mutex: tasks run one-at-a-time in submission order. A rejected
 * task never poisons the chain — the tail always continues to the next task.
 */
export const createSerialQueue = (): SerialQueue => {
  let tail: Promise<unknown> = Promise.resolve()
  return <T>(task: () => Promise<T>): Promise<T> => {
    const result = tail.then(task, task)
    tail = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }
}

export type KeyedQueue = <T>(key: string, task: () => Promise<T>) => Promise<T>

/**
 * One {@link SerialQueue} per key: same-key tasks run in order, different keys
 * run independently. Use to serialise read-modify-write critical sections that
 * share a backing store while letting unrelated stores proceed concurrently.
 */
export const createKeyedQueue = (): KeyedQueue => {
  const queues = new Map<string, SerialQueue>()
  return <T>(key: string, task: () => Promise<T>): Promise<T> => {
    const queue = queues.get(key) ?? createSerialQueue()
    queues.set(key, queue)
    return queue(task)
  }
}
